import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
// NOTE: `supabase` is dynamically imported inside `beforeLoad`, `loader`,
// and the component's `decide()` handler so the ~50 KiB
// @supabase/supabase-js bundle stays out of the main entry chunk. Because
// `beforeLoad` and `loader` are treated as "critical" route code and are
// NOT split by TanStack's autoCodeSplitting, a top-level static import here
// leaks supabase into every public route's initial JS payload.

// Beta Supabase OAuth helpers, typed shim so TS accepts them.
type OAuthClient = { name?: string; client_id?: string };
type OAuthDetails = {
  client?: OAuthClient;
  redirect_url?: string;
  redirect_to?: string;
  scope?: string;
  scopes?: string[];
};
type OAuthResult = { data: OAuthDetails | null; error: { message: string } | null };
type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<OAuthResult>;
  approveAuthorization: (id: string) => Promise<OAuthResult>;
  denyAuthorization: (id: string) => Promise<OAuthResult>;
};
async function oauthApi(): Promise<OAuthApi> {
  const { supabase } = await import("@/integrations/supabase/client");
  return (supabase.auth as unknown as { oauth: OAuthApi }).oauth;
}

const GOLD = "#C8A84B";

export const Route = createFileRoute("/.lovable/oauth/consent")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id:
      typeof s.authorization_id === "string" ? s.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      const next = location.pathname + location.searchStr;
      throw redirect({ to: "/login", search: { redirect: next } });
    }
  },
  loader: async ({ location }) => {
    const authorizationId = new URLSearchParams(location.search).get(
      "authorization_id",
    )!;
    const api = await oauthApi();
    const { data, error } = await api.getAuthorizationDetails(authorizationId);
    if (error) throw new Error(error.message);
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    return data;
  },
  component: Consent,
  errorComponent: ({ error }) => (
    <main className="flex min-h-dvh items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card/40 p-8 text-sm text-foreground">
        Could not load this authorization request:{" "}
        {String((error as Error)?.message ?? error)}
      </div>
    </main>
  ),
});

function Consent() {
  const details = Route.useLoaderData();
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const api = await oauthApi();
    const { data, error: err } = approve
      ? await api.approveAuthorization(authorization_id)
      : await api.denyAuthorization(authorization_id);
    if (err) {
      setBusy(false);
      setError(err.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "an app";
  const scopes =
    details?.scopes ??
    (details?.scope ? details.scope.split(/\s+/).filter(Boolean) : []);

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card/40 p-8">
        <p
          className="text-[10px] font-medium uppercase tracking-[0.22em]"
          style={{ color: GOLD }}
        >
          Authorize connection
        </p>
        <h1 className="mt-2 font-display text-2xl font-semibold tracking-tight text-foreground">
          Connect {clientName} to Shugert
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {clientName} will be able to call this app's enabled tools while you
          are signed in. This does not bypass Shugert's permissions or backend
          policies.
        </p>

        {scopes.length > 0 && (
          <ul className="mt-6 space-y-2 text-sm text-foreground">
            {scopes.map((s: string) => (
              <li key={s} className="rounded-lg border border-border px-3 py-2">
                {s === "openid"
                  ? "Verify your identity"
                  : s === "email"
                    ? "Share your email address"
                    : s === "profile"
                      ? "Share your basic profile"
                      : `Additional permission: ${s}`}
              </li>
            ))}
          </ul>
        )}

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <div className="mt-8 flex flex-col gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => decide(true)}
            className="w-full rounded-full px-6 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: GOLD }}
          >
            {busy ? "Working…" : "Approve"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => decide(false)}
            className="w-full rounded-full border border-border px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/40 disabled:opacity-50"
          >
            Cancel connection
          </button>
        </div>
      </div>
    </main>
  );
}